import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { LineChart } from 'lucide-react'
import type { Insights } from '../hooks/useInsights'

interface SearchVolumeChartProps {
  data: Insights['timeSeries'] | undefined
  isLoading: boolean
}

/**
 * Lightweight inline SVG line chart — no charting library dependency.
 * Shows searches (solid line) and zero-results (dashed line) over the period.
 */
export function SearchVolumeChart({ data, isLoading }: SearchVolumeChartProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <LineChart className="h-5 w-5" />
          Search Volume
        </CardTitle>
        <CardDescription>Daily searches and zero-result queries</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : !data || data.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
            No data yet
          </div>
        ) : (
          <Chart data={data} />
        )}
      </CardContent>
    </Card>
  )
}

function Chart({ data }: { data: Insights['timeSeries'] }) {
  const W = 600
  const H = 180
  const PAD = { l: 32, r: 8, t: 8, b: 20 }
  const innerW = W - PAD.l - PAD.r
  const innerH = H - PAD.t - PAD.b

  const max = Math.max(1, ...data.map((d) => Math.max(d.searches, d.zeroResults)))
  const step = innerW / Math.max(1, data.length - 1)

  const searchPath = data
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${PAD.l + i * step},${PAD.t + innerH - (d.searches / max) * innerH}`)
    .join(' ')
  const zeroPath = data
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${PAD.l + i * step},${PAD.t + innerH - (d.zeroResults / max) * innerH}`)
    .join(' ')

  // Y-axis ticks — 0, mid, max
  const ticks = [0, Math.round(max / 2), max]

  // X-axis labels — first, middle, last
  const xLabels = [data[0], data[Math.floor(data.length / 2)], data[data.length - 1]].filter(
    (d): d is (typeof data)[number] => d !== undefined
  )

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-48 w-full min-w-[480px]">
        {/* Gridlines */}
        {ticks.map((t) => {
          const y = PAD.t + innerH - (t / max) * innerH
          return (
            <g key={t}>
              <line
                x1={PAD.l}
                y1={y}
                x2={W - PAD.r}
                y2={y}
                stroke="currentColor"
                strokeOpacity="0.1"
                strokeDasharray="2 2"
              />
              <text
                x={PAD.l - 4}
                y={y + 3}
                textAnchor="end"
                className="fill-muted-foreground text-[10px]"
              >
                {t}
              </text>
            </g>
          )
        })}

        {/* Zero-result line (amber, dashed) */}
        <path d={zeroPath} fill="none" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="4 2" opacity="0.9" />

        {/* Search line (primary colour) */}
        <path d={searchPath} fill="none" stroke="#32373c" strokeWidth="2" className="dark:stroke-white" />

        {/* X axis labels */}
        {xLabels.map((d, i) => (
          <text
            key={d.date}
            x={PAD.l + (i * innerW) / 2}
            y={H - 4}
            textAnchor={i === 0 ? 'start' : i === xLabels.length - 1 ? 'end' : 'middle'}
            className="fill-muted-foreground text-[10px]"
          >
            {new Date(d.date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })}
          </text>
        ))}
      </svg>
      <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="h-0.5 w-4 bg-foreground" />
          Searches
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-0.5 w-4 border-t border-dashed border-amber-500" />
          Zero-results
        </div>
      </div>
    </div>
  )
}
